import {
  Flex,
  ListItem,
  Radio,
  RadioGroup,
  Stack,
  Text,
  TextProps,
  UnorderedList,
} from "@chakra-ui/react";
import Joi from "joi";
import { useEffect } from "react";

import useForm, { OnFormSubmitType } from "hooks/useForm";
import { TYPE_INSERT, TYPE_INSERT_KEY } from "./types";
import { refreshData } from "@ud/store/insertForm/insertSlice";
import { useAppSelector } from "hooks/reduxHooks";
import { useTracking } from "contexts/Tracking";
import CustomLink from "components/Chakra/CustomLink";
import { EOrderTypeId } from "utils/payment";

type ITypeInsert = {
  buttonNextText?: string;
  buttonPrevText?: string;
};

type IFormValue = {
  typePriceInsert: string;
};

const formSchema = Joi.object({
  typePriceInsert: Joi.string().required(),
});

const TypeInsertForm: React.FC<ITypeInsert> = () => {
  const { trackEvent } = useTracking();
  const insertReduxData = useAppSelector((e) => e.insertForm.data);

  const { handleSubmit, watch, setValue, errors, clearErrors } =
    useForm<IFormValue>(formSchema, {
      keepDataOnSuccess: true,
      defaultValues: {
        typePriceInsert: insertReduxData.typePriceInsert ?? undefined,
      },
    });
  const watchTypeInsert = watch("typePriceInsert");
  const handleData: OnFormSubmitType<IFormValue> = () => {
    refreshData();
    trackEvent({
      "dl.eCat": "offer_create",
      "dl.eAct": "offer.clicked",
      "dl.eLab": watchTypeInsert,
    });
  };

  useEffect(
    () =>
      trackEvent({
        "dl.eCat": "offer_create",
        "dl.eAct": "page.loaded",
        "dl.eLab": "Ceník",
      }),
    [trackEvent],
  );

  const listDescription = (descriptions: Array<string>, id: string) => {
    const getFontWeight = (description: string): TextProps["fontWeight"] => {
      switch (id) {
        case TYPE_INSERT_KEY.START:
          return "normal";
        case TYPE_INSERT_KEY.STANDARD:
          return descriptions[0] === description ? "bold" : "normal";
        case TYPE_INSERT_KEY.VIP:
          return "bold";
      }
    };

    return (
      <UnorderedList textAlign="left">
        {descriptions.map((description, index) => (
          <ListItem key={index}>
            <Text variant="medium" fontWeight={getFontWeight(description)}>
              {description}
            </Text>
          </ListItem>
        ))}
      </UnorderedList>
    );
  };

  const getQueryProps = (): string => {
    switch (watchTypeInsert as TYPE_INSERT_KEY) {
      case TYPE_INSERT_KEY.START:
        return EOrderTypeId.INSERT_OFFER_FREE.toString();
      case TYPE_INSERT_KEY.STANDARD:
        return EOrderTypeId.INSERT_OFFER_STANDARD.toString();
      case TYPE_INSERT_KEY.VIP:
        return EOrderTypeId.INSERT_OFFER_VIP.toString();
    }
  };

  return (
    <form
      onSubmit={handleSubmit(handleData)}
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        gap: 0,
        justifyContent: "space-between",
        width: "100%",
      }}
    >
      <RadioGroup
        value={watchTypeInsert}
        position="relative"
        display="flex"
        flexDirection="column"
        gap="10px"
      >
        {Object.entries(TYPE_INSERT).map((item) => {
          const isHighlighted = item[0] === watchTypeInsert;

          return (
            <Stack
              key={item[0]}
              borderColor={isHighlighted ? "ud-success" : "ud-grey-300"}
              borderWidth="1px"
              borderStyle="solid"
              p="16px"
              borderRadius="6px"
              backgroundColor={isHighlighted ? "ud-green-100" : "ud-white"}
              cursor="pointer"
              sx={{
                "label span:nth-of-type(1)": {
                  position: "relative",
                  top: 0,
                  transform: "translate(0, 50%)",
                  alignSelf: "flex-start",
                },
                "label span:nth-of-type(2)": {
                  flex: 1,
                },
              }}
              onClick={() => {
                if (errors.typePriceInsert?.message) {
                  clearErrors("typePriceInsert");
                }
                setValue("typePriceInsert", item[0]);
              }}
            >
              <Radio value={item[0]} colorScheme="radioColor" spacing="10px">
                <Stack direction="row" justify="space-between" align="center">
                  <Text
                    fontWeight="bold"
                    color={isHighlighted ? "ud-success" : "unset"}
                  >
                    {item[1].translationId}
                  </Text>
                  <Text variant="large" fontWeight="bold">
                    {item[1].price}
                  </Text>
                </Stack>
                {listDescription(item[1].descriptions, item[0])}
              </Radio>
            </Stack>
          );
        })}
        {errors.typePriceInsert?.message && (
          <Text
            variant="form-error"
            textAlign="center"
            position="absolute"
            bottom={-6}
            left={0}
          >
            Zaškrtněte jednu z možností.
          </Text>
        )}
      </RadioGroup>
      <Flex
        w="100%"
        alignSelf="center"
        mt={[null, "40px"]}
        p={["16px", 0]}
        direction="row"
        bg={["ud-white", "unset"]}
        zIndex={[1020, "unset"]}
        position={["fixed", "unset"]}
        left="0"
        bottom="0"
        justifyContent="center"
        gap={[null, "24px"]}
        borderTop={["1px solid", "unset"]}
        borderColor="ud-grey-300"
      >
        <CustomLink
          type="submit"
          variant={watchTypeInsert ? "button" : "disable"}
          to={watchTypeInsert ? "/platba/vlozeni-inzeratu" : undefined}
          queryProps={{
            tid: getQueryProps(),
            oid: insertReduxData.offerId?.toString() ?? "",
          }}
        >
          Zveřejnit
        </CustomLink>
      </Flex>
    </form>
  );
};

export default TypeInsertForm;
